import axios from 'axios'
import { createStandaloneToast } from '@chakra-ui/react'

import {
  AuthResponse,
  DataRecordsResponse,
  DataEntriesWithoutEntriesResponse,
  EntriesV2Response,
  AllDataRecordsResponse,
  FileUrlResponse,
  UploadFileResponse,
  GetMeResponse,
  SubscribeResponse,
  CancelResponse,
  AdminCheckResponse,
  WhitelistResponse,
  RemoveFromWhitelistResponse,
  AddToWhitelistResponse,
  CheckSubscriptionResponse,
  UpdateRecordSymbolsResponse,
} from '../types'
import { capitalize, removeQuotes } from '../utils/helpers'

const { toast } = createStandaloneToast()

// Create an axios instance
const api = axios.create({
  // baseURL: 'http://34.143.214.250:8080',
  baseURL: 'https://api.librealpha.com',

})

// Add request interceptor to add JWT token to headers
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('jwt_token')
    if (token) {
      config.headers.Authorization = `Bearer ${removeQuotes(token)}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Add response interceptor to handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Show toast notification
      toast({
        title: 'Session Expired',
        description: 'Please reconnect your wallet to continue',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })

      // Clear auth data from localStorage
      localStorage.removeItem('user_id')
      localStorage.removeItem('jwt_token')
      localStorage.removeItem('api_key')
      // Reload the page to trigger a full logout
      window.location.reload()
    }
    return Promise.reject(error)
  }
)

export const authService = {
  authenticate: async (
    wallet_address: string,
    signature: string,
    timestamp: string
  ) => {
    const response = await api.post<AuthResponse>('/api/users/auth', {
      wallet_address,
      signature,
      timestamp,
    })
    return response.data
  },
}

export const dataService = {
  getAllDataRecords: async (type: string) => {
    const capitalizedType = capitalize(type)
    // [MODIFIED] Removed /v2 from path
    const response = await api.get<AllDataRecordsResponse>(
      `/api/records/all?type=${capitalizedType}`
    )
    return response.data
  },
  getDataRecords: async () => {
    const response = await api.get<DataRecordsResponse>('/api/records')
    return response.data
  },
  addEntry: async (recordId: number, data: any) => {
    const response = await api.post('/api/entries/direct-add', {
      record_id: recordId,
      data: data
    });
    return response.data;
  },
  getDataEntriesWithoutEntries: async (recordId: number) => {
    // [MODIFIED] Removed /v2 from path
    const response = await api.get<DataEntriesWithoutEntriesResponse>(
      `/api/entries/without_entries`,
      {
        params: {
          record_id: recordId,
        },
      }
    )
    return response.data
  },
  getDataEntries: async (
    recordId: number,
    options?: {
      symbol?: string[]
      size?: number
      latest_data?: boolean
      start_timestamp?: number
    }
  ) => {
    // [MODIFIED] Removed /v2 from path
    
    const response = await api.get<EntriesV2Response>(`/api/entries/entries_only`, {
      params: {
        record_id: recordId,
        symbol: options?.symbol?.length ? options.symbol.join(',') : undefined,
        size: options?.size,
        latest_data: options?.latest_data,
        start_timestamp: options?.start_timestamp,
      },
    })
    return response.data
  },
  getAlphaEntries: async (
    recordId: number,
    options?: {
      symbol?: string[]
      size?: number
      latest_data?: boolean
      start_timestamp?: number
    }
  ) => {
    const response = await api.get<EntriesV2Response>(`/api/alpha/entries`, {
      params: {
        record_id: recordId,
        symbol: options?.symbol?.length ? options.symbol.join(',') : undefined,
        size: options?.size,
        latest_data: options?.latest_data,
        start_timestamp: options?.start_timestamp,
      },
    })
    return response.data
  },

  createDataRecord: async (
    name: string,
    description: string,
    target_asset: string,
    type: string,
    is_encrypted: boolean,
    update_frequency: number
  ) => {
    const response = await api.post('/api/records/create', {
      name,
      description,
      target_asset,
      type,
      is_encrypted,
      update_frequency,
    })
    return response.data
  },

  createDataRecordV2: async (params: {
    name: string
    description: string
    is_encrypted: boolean
    type: string
    update_frequency: number
    features: Array<{
      name: string
      description: string
      dataType: string
    }> | 'default'
    symbols?: string[]
    target_assets?: string[]
  }) => {
    // [MODIFIED] Updated to new flat structure with symbols and target_assets arrays
    const response = await api.post('/api/records/create', params)
    return response.data
  },

  getFileDownloadUrl: async (fileId: number) => {
    const response = await api.get<FileUrlResponse>(
      `/api/files/download-url?file_id=${fileId}`
    )
    return response.data
  },

  uploadFile: async (formData: FormData) => {
    const response = await api.post<UploadFileResponse>(
      `/api/files/upload`,
      formData
    )
    return response.data
  },

  getHoursWithData: async (params: {
    record_id: number
    symbol: string
    date: string
  }) => {
    const response = await api.get<{
      status: string
      data: {
        hours: string[]
        source: string
      }
    }>(`/api/records/hours-with-data`, {
      params,
    })
    return response.data
  },

  deleteHourData: async (params: {
    record_id: number
    symbol: string
    date: string
    hour: string
  }) => {
    const response = await api.post<{
      status: string
      data: {
        deleted: number
      }
      message: string
    }>(`/api/records/delete-hour`, params)
    return response.data
  },

  deleteRecord: async (record_id: number) => {
    const response = await api.post<CancelResponse>('/api/records/delete', {
      record_id,
    })
    return response.data
  },

  updateRecordSymbols: async (record_id: number, symbols: string[]) => {
    const response = await api.post<UpdateRecordSymbolsResponse>('/api/records/symbols/update', {
      record_id,
      symbols,
    })
    return response.data
  },

}

export const runnerService = {
  getRunnerList: async () => {
    const response = await api.get<AuthResponse>('/api/runners')
    return response.data
  },
}

export const userService = {
  getUser: async () => {
    const response = await api.get<GetMeResponse>('/api/users/me')
    return response.data
  },
  updateUser: async (name: string, contact: string) => {
    const response = await api.put<GetMeResponse>('/api/users/update', {
      name,
      contact,
    })
    return response.data
  },
}

export const subscriptionService = {
  // Get all subscriptions for current user
  getAllSubscriptions: async () => {
    const response = await api.get<CheckSubscriptionResponse>(
      '/api/subscriptions'
    )
    return response.data
  },

  // Subscribe to a record
  subscribe: async (record_id: number) => {
    const response = await api.post<SubscribeResponse>(
      '/api/subscriptions/subscribe',
      {
        record_id,
      }
    )
    return response.data
  },

  // Cancel a subscription using POST method
  cancelSubscription: async (record_id: number) => {
    const response = await api.post<CancelResponse>(
      '/api/subscriptions/cancel',
      {
        record_id,
      }
    )
    return response.data
  },
}

export const adminService = {
  // check admin permission
  check: async () => {
    const response = await api.get<AdminCheckResponse>('/api/admin/check')
    return response.data
  },
}


export const whitelistService = {
  getWhitelist: async () => {
    const response = await api.get<WhitelistResponse>('/api/whitelist/list')
    return response.data
  },
  removeFromWhitelist: async (wallet_address: string) => {
    const response = await api.delete<RemoveFromWhitelistResponse>('/api/whitelist/remove', {
      params: {
        wallet: wallet_address,
      }
    })
    return response.data
  },
  addToWhitelist: async ({wallet_address, description}: {wallet_address: string, description: string}) => {
    const response = await api.post<AddToWhitelistResponse>('/api/whitelist/add', {
      wallet_address,
      description,
    })
    return response.data
  },
}


export default api
